import React, { useEffect, useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import StatCard from '../components/StatCard';
import { Store as Stype, Transaction } from '../types';
import { DollarSign, Store, Receipt, User, TrendingUp, ArrowUpRight, Clock } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

interface DashboardProps {
  setCurrentPage: (page: string) => void;
}

const StoreLogoThumb: React.FC<{ logo?: string | null; name: string }> = ({ logo, name }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [logo]);

  if (logo && !errored) {
    return (
      <img
        src={logo}
        alt={`${name} logo`}
        onError={() => setErrored(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
      />
    );
  }
  return <Store size={18} color="#F1561D" />;
};

const Dashboard: React.FC<DashboardProps> = ({ setCurrentPage }) => {
  const primary = '#F1561D';
  const API_BASE = getApiBase();
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem('authUser') || '{}');
  const isManager = user.role === 'manager';
  const managerStoreId = user.storeId;

  const [stores, setStores] = useState<Stype[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<'week' | 'month' | 'year'>('week');

  const isToday = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  };

  // ── Normalise storeId to a plain string regardless of whether the API
  //    returns it as a string or a populated object { _id, name } ──────────────
  const getStoreId = (storeId: any): string => {
    if (!storeId) return '';
    if (typeof storeId === 'string') return storeId;
    if (typeof storeId === 'object') return storeId._id ?? '';
    return '';
  };

  // ── Filter transactions to the manager's store if applicable ─────────────
  const managerTransactions = useMemo(() => {
    if (!isManager || !managerStoreId) return transactions;
    return transactions.filter(tx => getStoreId(tx.storeId) === managerStoreId);
  }, [transactions, isManager, managerStoreId]);

  const filteredTransactions = useMemo(() => {
    const now = new Date();
    return managerTransactions.filter(tx => {
      const txDate = new Date(tx.createdAt);
      if (range === 'week') {
        const ago = new Date(); ago.setDate(now.getDate() - 7);
        return txDate >= ago;
      }
      if (range === 'month') return txDate.getMonth() === now.getMonth() && txDate.getFullYear() === now.getFullYear();
      if (range === 'year') return txDate.getFullYear() === now.getFullYear();
      return true;
    });
  }, [managerTransactions, range]);

  const storeSalesToday = useMemo(() => {
    // Managers only see their own store
    const visibleStores = isManager
      ? stores.filter(s => s._id === managerStoreId)
      : stores;

    return visibleStores.map(store => {
      const total = transactions
        .filter(tx => getStoreId(tx.storeId) === store._id && isToday(tx.createdAt))
        .reduce((sum, tx) => sum + tx.total, 0);
      return { ...store, todaySales: total } as Stype & { todaySales: number };
    });
  }, [stores, transactions, isManager, managerStoreId]);

  const totalSales = filteredTransactions
    .filter(t => t.status === 'completed')
    .reduce((sum, tx) => sum + tx.total, 0);

  const totalTransactions = filteredTransactions.length;

      const avgCheckoutSeconds = useMemo(() => {
      const withDuration = filteredTransactions.filter(
        tx => typeof (tx as any).checkoutDurationSeconds === 'number'
          && (tx as any).checkoutDurationSeconds > 0
          && tx.status === 'completed'
      );
      if (withDuration.length === 0) return null;
      const sum = withDuration.reduce(
        (acc, tx) => acc + (tx as any).checkoutDurationSeconds, 0
      );
      return Math.round(sum / withDuration.length);
    }, [filteredTransactions]);

    const formatDuration = (seconds: number): string => {
      if (seconds < 60) return `${seconds}s`;
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return s > 0 ? `${m}m ${s}s` : `${m}m`;
    };

  const todayTotal = managerTransactions
    .filter(tx => isToday(tx.createdAt) && tx.status === 'completed')
    .reduce((sum, tx) => sum + tx.total, 0);

  const todayCount = managerTransactions.filter(tx => isToday(tx.createdAt)).length;

  // Aggregate chart data by date
  const chartData = useMemo(() => {
    const byDate: { [key: string]: number } = {};
    filteredTransactions.filter(t => t.status === 'completed').forEach(tx => {
      const d = new Date(tx.createdAt).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
      byDate[d] = (byDate[d] || 0) + tx.total;
    });
    return Object.entries(byDate)
      .map(([date, sales]) => ({ date, sales: Math.round(sales) }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [filteredTransactions]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        let storesData = data.data || data;
        if (isManager) storesData = storesData.filter((s: any) => s._id === managerStoreId);
        else storesData = storesData.filter((s: any) => s.isActive);
        setStores(storesData);
      } catch { setStores([]); } finally { setLoading(false); }
    };

    const fetchTransactions = async () => {
      try {
        // Managers fetch only their store's transactions via query param
        const url = isManager && managerStoreId
          ? `${API_BASE}/api/transactions?storeId=${managerStoreId}`
          : `${API_BASE}/api/transactions`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setTransactions(data.data || []);
      } catch { setTransactions([]); }
    };

    fetchStores();
    fetchTransactions();
  }, [API_BASE, token, isManager, managerStoreId]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const managerStoreName = isManager
    ? stores.find(s => s._id === managerStoreId)?.name ?? 'Your Store'
    : null;

  return (
    <div style={{ padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@500&display=swap');`}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {greeting()}, {user.name || 'Admin'}
          </p>
          <h1 style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>
            Dashboard {managerStoreName && <span style={{ fontSize: '16px', color: '#9ca3af', fontWeight: 400 }}>· {managerStoreName}</span>}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '8px 14px' }}>
          <Clock size={14} color="#9ca3af" />
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            {new Date().toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* ── Today banner ───────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #F1561D 0%, #ff8a5b 100%)',
        borderRadius: '16px', padding: '28px 32px', marginBottom: '28px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '16px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: '-40px', top: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', right: '60px', bottom: '-60px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.75)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Today's Revenue{managerStoreName ? ` · ${managerStoreName}` : ''}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: '42px', fontWeight: 600, color: '#fff', lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>
            R {todayTotal.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '32px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Transactions today</p>
            <p style={{ margin: '4px 0 0', fontSize: '24px', fontWeight: 600, color: '#fff' }}>{todayCount}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
              {isManager ? 'Store' : 'Active stores'}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '24px', fontWeight: 600, color: '#fff' }}>
              {isManager ? 1 : stores.length}
            </p>
          </div>
        </div>
      </div>

      {/* ── Stat cards ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '28px' }}>
        {[
          { label: 'Total Sales', value: `R ${totalSales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sub: `${range} period`, icon: <TrendingUp size={18} />, accent: '#F1561D', bg: '#fff5f2' },
          { label: 'Total Transactions', value: totalTransactions, sub: `${range} period`, icon: <Receipt size={18} />, accent: '#6366f1', bg: '#f5f3ff' },
          { label: isManager ? 'Your Store' : 'Active Stores', value: isManager ? managerStoreName ?? '—' : stores.length, sub: isManager ? 'Store view' : 'Currently active', icon: <Store size={18} />, accent: '#0ea5e9', bg: '#f0f9ff' },
          {
          label: 'Avg. Checkout Time',
          value: avgCheckoutSeconds !== null ? formatDuration(avgCheckoutSeconds) : '—',
          sub: `${range} period · completed`,
          icon: <Clock size={18} />,
          accent: '#059669',
          bg: '#f0fdf4'
        }
        ].map(card => (
          <div key={card.label} style={{
            background: '#fff', borderRadius: '14px', padding: '20px 24px',
            border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '12px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ background: card.bg, borderRadius: '10px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.accent }}>
                {card.icon}
              </div>
              <ArrowUpRight size={14} color="#d1d5db" />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>{card.label}</p>
              <p style={{ margin: '4px 0 0', fontSize: '26px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{card.value}</p>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>{card.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Chart + Recent Transactions ────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>

        {/* Chart */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>Sales Overview</h3>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as 'week' | 'month' | 'year')}
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none' }}
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => [`R ${Number(v).toLocaleString()}`, 'Sales']}
                  contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: '13px' }}
                />
                <Line type="monotone" dataKey="sales" stroke={primary} strokeWidth={2.5} dot={{ r: 4, fill: primary, strokeWidth: 0 }} activeDot={{ r: 6, fill: primary }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
              No data for selected period
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>Recent</h3>
            <button onClick={() => setCurrentPage('transactions')}
              style={{ fontSize: '12px', color: primary, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              View all →
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1 }}>
            {managerTransactions.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>No transactions yet</p>
            ) : managerTransactions
              .slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .slice(0, 6)
              .map(tx => {
                const storeId = getStoreId(tx.storeId);
                const store = stores.find(s => s._id === storeId);
                const storeName = store?.name ?? (typeof tx.storeId === 'object' ? (tx.storeId as any).name : 'Unknown');
                const initials = (tx.customer.name || '?').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                const colors = ['#fee2e2','#fef3c7','#d1fae5','#dbeafe','#ede9fe','#fce7f3'];
                const textColors = ['#dc2626','#d97706','#059669','#2563eb','#7c3aed','#db2777'];
                const ci = (tx.customer.name || '').charCodeAt(0) % colors.length;
                return (
                  <div key={tx._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', borderRadius: '10px', transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: colors[ci], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, color: textColors[ci], flexShrink: 0 }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.customer.name}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af' }}>{storeName}</p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#111827' }}>R {tx.total.toFixed(0)}</p>
                      <span style={{
                        fontSize: '10px', padding: '2px 7px', borderRadius: '20px', fontWeight: 500,
                        background: tx.status === 'completed' ? '#d1fae5' : tx.status === 'pending' ? '#fef3c7' : '#fee2e2',
                        color: tx.status === 'completed' ? '#065f46' : tx.status === 'pending' ? '#92400e' : '#991b1b',
                      }}>
                        {tx.status}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* ── Store Performance ──────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #f3f4f6' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
          Store Performance — Today{managerStoreName ? ` · ${managerStoreName}` : ''}
        </h3>
        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>Loading stores...</p>
        ) : storeSalesToday.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>No store data found.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
            {storeSalesToday.map(store => (
              <div key={store._id}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderRadius: '12px', border: '1px solid #f3f4f6', transition: 'border-color 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = '#F1561D')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#f3f4f6')}>

                {/* ── Logo or fallback ── */}
                <div style={{
                  width: '42px', height: '42px', borderRadius: '10px',
                  background: (store as any).logo ? 'transparent' : '#fff5f2',
                  border: (store as any).logo ? '1px solid #f3f4f6' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, overflow: 'hidden',
                }}>
                  <StoreLogoThumb logo={(store as any).logo} name={store.name} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{store.name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: store.todaySales > 0 ? '#059669' : '#9ca3af', fontWeight: store.todaySales > 0 ? 500 : 400 }}>
                    R {store.todaySales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} today
                  </p>
                </div>
                <span style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '20px', background: '#d1fae5', color: '#065f46', fontWeight: 500, flexShrink: 0 }}>
                  Active
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default Dashboard;
